import { appConfig } from "@common/config/app.config";
import { AnalyticsService } from "@common/services/analytics-service";
import { effectiveConsent } from "@common/services/consent/store";
import { ensureGtag, markVendorLoaded, onConsentChange, updateConsentMode } from "@common/services/consent/loaders";
import { storeAttributionData } from "@common/utils/attribution";
import { isExtensionContext } from "@common/services/detect-context";
import type { ConsentChoices } from "@common/services/consent/contract";
import { loadCrisp } from "@common/services/consent/crisp";
import { isMetaPixelHost } from "@common/services/meta-pixel-dispatch";

/**
 * Consent-gated vendor loader. Applies the effective consent: the stored choice, or the jurisdiction
 * default (opt-out locations get analytics and marketing, GPC strips marketing; opt-in and unknown
 * locations get nothing until a choice is stored). Each vendor is injected at most once per page, the
 * first time its category is granted (spec sections 4, 5). Extension contexts never call this (spec section 9).
 */

function loadGoogleAnalytics(): void {
  const gaId = appConfig.GA_ID;
  if (!gaId || !markVendorLoaded("ga4")) return;
  const gtag = ensureGtag();
  const hostname = window.location.hostname;
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1" || hostname.includes(".local");
  gtag("js", new Date());
  // DO NOT override client_id in gtag('config'): it breaks GA4's session_start. See docs/attribution/GA4-PROTOCOL-REFERENCE.md
  // Cross-domain linker (#411): accept the _gl param blueticks.co puts on its links to this app, so the
  // visit keeps its original source instead of re-attributing to "Referral"/"Direct".
  gtag("config", gaId, {
    cookie_domain: isLocal ? "none" : ".blueticks.co",
    linker: { domains: ["blueticks.co", "app.blueticks.co"], accept_incoming: true },
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(s);
  // Flush analytics events queued before gtag loaded (same behaviour as before the gate).
  setTimeout(() => {
    try {
      new AnalyticsService().flushGtagQueue();
    } catch (e) {
      console.warn("[injectPixels] Failed to flush gtag queue:", e);
    }
  }, 100);
}

function loadHotjar(): void {
  if (!appConfig.HJ_ID || !markVendorLoaded("hotjar")) return;
  const s = document.createElement("script");
  s.innerHTML = `
    (function (h, o, t, j, a, r) {
      h.hj = h.hj || function () { (h.hj.q = h.hj.q || []).push(arguments) };
      h._hjSettings = { hjid: ${appConfig.HJ_ID}, hjsv: 6 };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script'); r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  `;
  document.head.appendChild(s);
}

function loadClarity(): void {
  if (!appConfig.CLARITY_ID || !markVendorLoaded("clarity")) return;
  const s = document.createElement("script");
  s.innerHTML = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${appConfig.CLARITY_ID}");
  `;
  document.body.appendChild(s);
}

function loadMetaPixel(): void {
  if (!appConfig.FB_PIXEL_ID || !isMetaPixelHost(window.location.hostname) || !markVendorLoaded("meta-pixel")) return;
  const s = document.createElement("script");
  s.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${appConfig.FB_PIXEL_ID}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(s);
}

function loadTikTok(): void {
  if (!appConfig.TIKTOK_ID || !markVendorLoaded("tiktok")) return;
  const s = document.createElement("script");
  s.innerHTML = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=i+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
      ttq.load('${appConfig.TIKTOK_ID}');
      ttq.page();
    }(window, document, 'ttq');
  `;
  document.body.appendChild(s);
}

/** Injects every vendor the choices allow. Idempotent: repeated grants inject nothing new. */
export function applyConsent(choices: ConsentChoices): void {
  if (typeof window === "undefined") return;
  updateConsentMode(choices);
  if (choices.analytics) {
    loadGoogleAnalytics();
    loadHotjar();
    loadClarity();
  }
  if (choices.marketing) {
    loadMetaPixel();
    loadTikTok();
    // Attribution cookie and backup are written only once marketing is granted (spec section 5 step 4).
    storeAttributionData();
  }
  // Crisp is functional, but only loads on the visitor's own click or after a choice (spec section 2.1).
  if (choices.analytics || choices.marketing) loadCrispWidget();
}

function loadCrispWidget(): void {
  if (!appConfig.CRISP_ID || !markVendorLoaded("crisp")) return;
  const isRtl = document.documentElement.getAttribute("dir") === "rtl";
  const w = window as unknown as { $crisp?: unknown[] };
  w.$crisp = w.$crisp || [];
  w.$crisp.push(["config", "position:reverse", [isRtl]]);
  loadCrisp().catch(() => undefined);
}

let started = false;

/**
 * Entry point, called once from the website shell. Applies the effective consent (stored choice, or the
 * jurisdiction default) and re-applies on every consent change without a reload.
 */
export function injectPixels(): void {
  if (isExtensionContext() || typeof window === "undefined") return;
  if (started) return;
  started = true;
  ensureGtag();
  // Effective consent: the stored choice, or the jurisdiction default (opt-out regions get analytics
  // by default; opt-in and unknown locations get nothing until a choice is stored).
  const apply = () => applyConsent(effectiveConsent().choices);
  apply();
  onConsentChange(apply);
}
